import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { dashboardAPI, dossiersAPI, dashboardConfigAPI, permissionsAPI } from '../services/api';
import { Link } from 'react-router-dom';
import { 
  FolderOpen, Bell, Clock, CheckCircle, AlertTriangle, TrendingUp,
  Users, Activity, FileText, FileWarning, Smile, BarChart3, Calendar,
  LayoutDashboard, ArrowRight
} from 'lucide-react';

// Map des icônes
const iconMap = {
  LayoutDashboard, BarChart3, FileText, Users, Bell, AlertTriangle,
  Activity, FileWarning, Smile, Clock, CheckCircle, FolderOpen, 
  TrendingUp, Calendar
};

const DynamicDashboard = () => {
  const { user, isSuperAdmin } = useAuth();
  const [widgets, setWidgets] = useState([]);
  const [stats, setStats] = useState(null);
  const [teamLoad, setTeamLoad] = useState([]);
  const [recentDossiers, setRecentDossiers] = useState([]);
  const [activityLogs, setActivityLogs] = useState([]);
  const [taches, setTaches] = useState([]);
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newUnviewedCount, setNewUnviewedCount] = useState(0);
  const [newUnviewedDossiers, setNewUnviewedDossiers] = useState([]);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      // Charger les widgets configurés
      const widgetsRes = await dashboardConfigAPI.getVisibleWidgets();
      setWidgets(widgetsRes.data);

      // Charger les données en parallèle
      // Si c'est un gestionnaire (pas super_admin), filtrer par son ID
      const gestionnaireIdFilter = isSuperAdmin ? null : user?.id;
      
      const [statsRes, teamRes, dossiersRes] = await Promise.all([
        dashboardAPI.getStats(gestionnaireIdFilter),
        dashboardAPI.getTeamLoad(),
        dossiersAPI.getAll({ limit: 10 })
      ]);

      setStats(statsRes.data);
      setTeamLoad(teamRes.data);
      setRecentDossiers(dossiersRes.data);

      // Charger les nouveaux non vus pour l'alerte
      try {
        const token = localStorage.getItem('token');
        const unviewedRes = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/dossiers?statut=new&is_viewed=false&limit=10`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (unviewedRes.ok) {
          const unviewedData = await unviewedRes.json();
          setNewUnviewedDossiers(unviewedData.filter(d => !d.is_viewed && d.statut === 'new'));
          setNewUnviewedCount(unviewedData.filter(d => !d.is_viewed && d.statut === 'new').length);
        }
      } catch (e) {
        console.error('Erreur chargement nouveaux:', e);
      }

      // Charger la météo si le widget header est actif
      const headerWidget = widgetsRes.data.find(w => w.code === 'header_bienvenue');
      if (headerWidget) {
        const ville = headerWidget.config?.ville_meteo || 'paris';
        try {
          const weatherRes = await dashboardConfigAPI.getWeather(ville);
          setWeather(weatherRes.data);
        } catch (e) {
          console.log('Météo non disponible');
        }
      }

      // Charger l'activité si le widget est actif
      if (widgetsRes.data.some(w => w.code === 'activite_recente' && w.visible)) {
        try {
          const activityRes = await permissionsAPI.getActivityLogs({ limit: 10 });
          setActivityLogs(activityRes.data);
        } catch (e) {
          console.log('Activité non disponible');
        }
      }
    } catch (error) {
      console.error('Erreur chargement dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour >= 6 && hour < 12) return 'Bonjour';
    if (hour >= 12 && hour < 18) return 'Bon après-midi';
    return 'Bonsoir'; // 18h-6h = Bonsoir (inclut la nuit)
  };

  const getStatusBadge = (statut) => {
    const badges = {
      new: { label: 'Nouveau', class: 'bg-blue-100 text-blue-700' },
      progress: { label: 'En cours', class: 'bg-yellow-100 text-yellow-700' },
      inprog: { label: 'En cours', class: 'bg-yellow-100 text-yellow-700' },
      done: { label: 'Clôturé', class: 'bg-green-100 text-green-700' },
      reject: { label: 'Refusé', class: 'bg-red-100 text-red-700' }
    };
    const badge = badges[statut] || badges.new;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-semibold ${badge.class}`}>
        {badge.label}
      </span>
    );
  };

  const getPriorityBadge = (priorite) => {
    const badges = {
      high: { icon: '⚠️', class: 'text-red-600' },
      med: { icon: '🟠', class: 'text-yellow-600' },
      low: { icon: '🟢', class: 'text-green-600' }
    };
    return badges[priorite] || badges.med;
  };

  const formatTimeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMins = Math.floor((now - date) / 60000);
    if (diffMins < 1) return "À l'instant";
    if (diffMins < 60) return `Il y a ${diffMins} min`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'short' });
  };

  // ========================================
  // RENDU DES WIDGETS
  // ========================================

  const renderWidget = (widget) => {
    switch (widget.code) {
      case 'header_bienvenue':
        return renderHeaderWidget(widget);
      case 'stats_globales':
        return renderStatsWidget(widget);
      case 'taches_prioritaires':
        return renderPriorityTasksWidget(widget);
      case 'charge_equipe':
        return renderTeamLoadWidget(widget);
      case 'dossiers_recents':
        return renderRecentDossiersWidget(widget);
      case 'activite_recente':
        return renderActivityWidget(widget);
      case 'alertes_documents':
        return renderAlertsWidget(widget);
      case 'rappels_taches':
        return renderRemindersWidget(widget);
      default:
        return null;
    }
  };

  // Widget Header - Design moderne avec gradient et météo réelle
  const renderHeaderWidget = (widget) => {
    const config = widget.config || {};
    const texteMotivant = config.texte_motivant || "Prêt pour une nouvelle journée productive ?";
    
    const today = new Date().toLocaleDateString('fr-FR', { 
      weekday: 'long', day: 'numeric', month: 'long' 
    });

    // Utiliser la vraie météo si disponible
    const meteoDisplay = weather ? {
      icon: weather.icon,
      label: `${weather.label}${weather.temperature !== null ? ` • ${weather.temperature}°C` : ''}`,
      color: weather.color || 'text-white/80',
      city: weather.city
    } : {
      icon: '☀️',
      label: 'Chargement...',
      color: 'text-white/60',
      city: ''
    };

    return (
      <div key={widget.code} className="col-span-full">
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-rose-500 via-pink-500 to-violet-600 p-8 text-white shadow-xl">
          {/* Décoration de fond */}
          <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full -translate-y-32 translate-x-32 blur-3xl"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/10 rounded-full translate-y-24 -translate-x-24 blur-2xl"></div>
          
          <div className="relative z-10">
            {/* Ligne supérieure : Météo + Date */}
            <div className="flex items-center justify-between mb-6">
              <div className={`flex items-center gap-2 ${meteoDisplay.color}`}>
                <span className="text-2xl">{meteoDisplay.icon}</span>
                <span className="text-sm font-medium opacity-90">
                  {meteoDisplay.label}
                  {meteoDisplay.city && <span className="opacity-70"> à {meteoDisplay.city}</span>}
                </span>
              </div>
              {config.afficher_date !== false && (
                <div className="flex items-center gap-2 text-white/70">
                  <Calendar className="w-4 h-4" />
                  <span className="text-sm capitalize">{today}</span>
                </div>
              )}
            </div>
            
            {/* Message principal */}
            <div className="space-y-2">
              <h1 className="text-4xl font-bold tracking-tight">
                {getGreeting()}, {user?.prenom} ! <span className="inline-block animate-bounce">👋</span>
              </h1>
              <p className="text-xl text-white/80 font-light">
                {texteMotivant}
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Widget Stats
  const renderStatsWidget = (widget) => {
    const config = widget.config || {};
    const statsVisibles = config.stats_visibles || ['total', 'new', 'inprog', 'done'];
    
    const allStats = [
      { code: 'total', label: 'Total dossiers', value: stats?.total || 0, icon: FolderOpen, color: 'blue' },
      { code: 'new', label: 'Nouveaux', value: stats?.new || 0, icon: Bell, color: 'indigo' },
      { code: 'inprog', label: 'En cours', value: stats?.inprog || 0, icon: Clock, color: 'yellow' },
      { code: 'progress', label: 'En traitement', value: stats?.progress || 0, icon: TrendingUp, color: 'orange' },
      { code: 'done', label: 'Clôturés', value: stats?.done || 0, icon: CheckCircle, color: 'green' },
      { code: 'reject', label: 'Refusés', value: stats?.reject || 0, icon: AlertTriangle, color: 'red' },
      { code: 'high_priority', label: 'Priorité haute', value: stats?.high_priority || 0, icon: AlertTriangle, color: 'red' },
    ];

    const visibleStats = allStats.filter(s => statsVisibles.includes(s.code));

    const colorClasses = {
      blue: 'bg-blue-50 text-blue-600',
      indigo: 'bg-indigo-50 text-indigo-600',
      yellow: 'bg-yellow-50 text-yellow-600',
      orange: 'bg-orange-50 text-orange-600',
      green: 'bg-green-50 text-green-600',
      red: 'bg-red-50 text-red-600',
    };

    return (
      <div key={widget.code} className="col-span-full">
        <div className={`grid grid-cols-2 md:grid-cols-${Math.min(visibleStats.length, 4)} lg:grid-cols-${Math.min(visibleStats.length, 6)} gap-4`}>
          {visibleStats.map(stat => {
            const Icon = stat.icon;
            return (
              <div key={stat.code} className={`rounded-xl p-5 ${colorClasses[stat.color]?.split(' ')[0] || 'bg-gray-50'}`}>
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${colorClasses[stat.color] || 'bg-gray-100 text-gray-600'}`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div>
                    <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
                    <div className="text-sm text-gray-600">{stat.label}</div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Widget Tâches prioritaires
  const renderPriorityTasksWidget = (widget) => {
    const config = widget.config || {};
    const limite = config.limite || 5;
    const filtrerStatut = config.filtrer_statut || ['new', 'inprog', 'progress'];
    
    const priorityDossiers = recentDossiers
      .filter(d => d.priorite === 'high' && filtrerStatut.includes(d.statut))
      .slice(0, limite);

    return (
      <div key={widget.code} className={widget.largeur === 'full' ? 'col-span-full' : 'col-span-1'}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-500" />
              {widget.label}
            </h2>
            <span className="bg-red-100 text-red-700 px-3 py-1 rounded-full text-sm font-semibold">
              {priorityDossiers.length}
            </span>
          </div>

          <div className="space-y-3">
            {priorityDossiers.map(dossier => (
              <Link
                key={dossier.id}
                to={`/dossiers/${dossier.id}`}
                className="block p-4 bg-gray-50 hover:bg-gray-100 rounded-lg transition"
              >
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-semibold text-gray-900">{dossier.reference}</div>
                    <div className="text-sm text-gray-600">
                      {dossier.identite?.prenom} {dossier.identite?.nom}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {getStatusBadge(dossier.statut)}
                    <span className="text-red-500">⚠️</span>
                  </div>
                </div>
              </Link>
            ))}
            {priorityDossiers.length === 0 && config.afficher_vide !== false && (
              <p className="text-center text-gray-500 py-8">🎉 Aucune tâche prioritaire</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Widget Charge équipe
  const renderTeamLoadWidget = (widget) => {
    const config = widget.config || {};
    const limite = config.limite || 5;

    return (
      <div key={widget.code} className={widget.largeur === 'full' ? 'col-span-full' : 'col-span-1'}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="w-5 h-5 text-violet-500" />
            {widget.label}
          </h2>

          <div className="space-y-3">
            {teamLoad.slice(0, limite).map(gest => (
              <div key={gest.id} className="flex items-center gap-4">
                {config.afficher_avatar !== false && (
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center text-white font-semibold flex-shrink-0"
                    style={{ background: gest.avatar_color }}
                  >
                    {gest.prenom?.[0]}{gest.nom?.[0]}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    {gest.prenom} {gest.nom}
                  </div>
                  <div className="text-xs text-gray-500">
                    {gest.role === 'senior' ? 'Senior' : 'Gestionnaire'}
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold text-gray-900">{gest.dossiers_count}</div>
                  <div className="text-xs text-gray-500">dossiers</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  // Widget Dossiers récents
  const renderRecentDossiersWidget = (widget) => {
    const config = widget.config || {};
    const limite = config.limite || 5;
    const colonnes = config.colonnes || ['reference', 'client', 'caisse', 'statut', 'priorite', 'gestionnaire'];

    return (
      <div key={widget.code} className="col-span-full">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-cyan-500" />
            {widget.label}
          </h2>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-gray-200">
                  {colonnes.includes('reference') && <th className="text-left py-3 px-4 font-semibold text-gray-700">Référence</th>}
                  {colonnes.includes('client') && <th className="text-left py-3 px-4 font-semibold text-gray-700">Client</th>}
                  {colonnes.includes('caisse') && <th className="text-left py-3 px-4 font-semibold text-gray-700">Caisse</th>}
                  {colonnes.includes('statut') && <th className="text-left py-3 px-4 font-semibold text-gray-700">Statut</th>}
                  {colonnes.includes('priorite') && <th className="text-left py-3 px-4 font-semibold text-gray-700">Priorité</th>}
                  {colonnes.includes('gestionnaire') && <th className="text-left py-3 px-4 font-semibold text-gray-700">Gestionnaire</th>}
                </tr>
              </thead>
              <tbody>
                {recentDossiers.slice(0, limite).map(dossier => (
                  <tr key={dossier.id} className="border-b border-gray-100 hover:bg-gray-50 transition">
                    {colonnes.includes('reference') && (
                      <td className="py-3 px-4">
                        <Link to={`/dossiers/${dossier.id}`} className="font-semibold text-green-600 hover:underline">
                          {dossier.reference}
                        </Link>
                      </td>
                    )}
                    {colonnes.includes('client') && (
                      <td className="py-3 px-4">{dossier.identite?.prenom} {dossier.identite?.nom}</td>
                    )}
                    {colonnes.includes('caisse') && (
                      <td className="py-3 px-4 text-sm text-gray-600">{dossier.caisse}</td>
                    )}
                    {colonnes.includes('statut') && (
                      <td className="py-3 px-4">{getStatusBadge(dossier.statut)}</td>
                    )}
                    {colonnes.includes('priorite') && (
                      <td className="py-3 px-4 text-center">
                        <span className={getPriorityBadge(dossier.priorite).class}>
                          {getPriorityBadge(dossier.priorite).icon}
                        </span>
                      </td>
                    )}
                    {colonnes.includes('gestionnaire') && (
                      <td className="py-3 px-4 text-sm text-gray-600">{dossier.gestionnaire_nom || 'Non assigné'}</td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-4 text-center">
            <Link to="/dossiers" className="inline-flex items-center gap-2 text-green-600 hover:text-green-700 font-semibold">
              Voir tous les dossiers <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </div>
    );
  };

  // Widget Activité récente
  const renderActivityWidget = (widget) => {
    const config = widget.config || {};
    const limite = config.limite || 5;

    const getActivityIcon = (type) => {
      switch (type) {
        case 'connexion': return '🔑';
        case 'dossier_creation': return '📁';
        case 'dossier_modification': return '✏️';
        case 'statut_change': return '🔄';
        case 'document_upload': return '📤';
        default: return '📌';
      }
    };

    return (
      <div key={widget.code} className={widget.largeur === 'full' ? 'col-span-full' : 'col-span-1'}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Activity className="w-5 h-5 text-amber-500" />
            {widget.label}
          </h2>

          <div className="space-y-3">
            {activityLogs.slice(0, limite).map((log, idx) => (
              <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                <span className="text-xl">{getActivityIcon(log.type)}</span>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-900 truncate">{log.description}</div>
                  <div className="text-xs text-gray-500">{log.user_nom}</div>
                </div>
                <div className="text-xs text-gray-400">{formatTimeAgo(log.date)}</div>
              </div>
            ))}
            {activityLogs.length === 0 && (
              <p className="text-center text-gray-500 py-8">Aucune activité récente</p>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Widget Alertes
  const renderAlertsWidget = (widget) => {
    return (
      <div key={widget.code} className={widget.largeur === 'full' ? 'col-span-full' : 'col-span-1'}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileWarning className="w-5 h-5 text-orange-500" />
            {widget.label}
          </h2>
          <p className="text-center text-gray-500 py-8">Aucune alerte</p>
        </div>
      </div>
    );
  };

  // Widget Rappels
  const renderRemindersWidget = (widget) => {
    return (
      <div key={widget.code} className={widget.largeur === 'full' ? 'col-span-full' : 'col-span-1'}>
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-full">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <Bell className="w-5 h-5 text-pink-500" />
            {widget.label}
          </h2>
          <p className="text-center text-gray-500 py-8">Aucun rappel</p>
        </div>
      </div>
    );
  };

  // ========================================
  // RENDU PRINCIPAL
  // ========================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dynamic-dashboard">
      {/* Alerte Nouveaux Clients Non Vus */}
      {newUnviewedCount > 0 && (
        <div className="bg-gradient-to-r from-red-500 to-rose-500 rounded-xl p-4 shadow-lg animate-pulse">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
                <Bell className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-white font-bold text-lg">
                  🚨 {newUnviewedCount} nouveau{newUnviewedCount > 1 ? 'x' : ''} client{newUnviewedCount > 1 ? 's' : ''} en attente !
                </h3>
                <p className="text-white/80 text-sm">
                  {newUnviewedDossiers.slice(0, 3).map(d => 
                    `${d.identite?.prenom || ''} ${d.identite?.nom || ''}`
                  ).join(', ')}
                  {newUnviewedCount > 3 && ` et ${newUnviewedCount - 3} autre${newUnviewedCount - 3 > 1 ? 's' : ''}...`}
                </p>
              </div>
            </div>
            <Link 
              to="/dossiers/nouveaux" 
              className="bg-white text-red-600 px-4 py-2 rounded-lg font-semibold hover:bg-red-50 transition flex items-center gap-2"
            >
              Voir les nouveaux
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      )}
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {widgets.map(widget => renderWidget(widget))}
      </div>
    </div>
  );
};

export default DynamicDashboard;
